<script setup lang="ts">
/**
 * Shown while the shell runs on an impersonation session (I-2327): whose
 * account this is, who opened it and when master ends it, plus the way back
 * to the impersonator's own account. Renders nothing otherwise.
 *
 * The same banner serves the support operator of a remote support session
 * (Fernwartung): „Fernwartung für <Firma> als <Konto> bis <Zeit>“, and its
 * „Beenden“ signs the session out — there is no account to return to.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NxBanner, NxButton, formatTime } from '@blpde/nx-ui';
import { endImpersonation, signOut, useShellUser } from '@/auth';
import { companyLabel, useSessionScope } from '@/scope';

const { t, locale } = useI18n();
const { user } = useShellUser();
const scope = useSessionScope();
const leaving = ref(false);

const impersonation = computed(() => user.value?.impersonation ?? null);
const support = computed(() => user.value?.support ?? null);
const shown = computed(() => impersonation.value ?? support.value);

// The company a remote support session works for is the session's own.
onMounted(() => {
  if (support.value) void scope.load();
});
const company = computed(() => {
  const active = scope.active.value;
  return active ? companyLabel(active.company) : '';
});

const endTime = computed(() =>
  formatTime(impersonation.value?.expiresAt ?? support.value?.endsAt, { locale: locale.value })
);

const text = computed(() => {
  const session = support.value;
  if (session && !impersonation.value) {
    const params = {
      name: user.value?.name || user.value?.email || '',
      company: company.value,
      time: endTime.value,
    };
    const key = company.value
      ? endTime.value
        ? 'banner'
        : 'bannerWithoutEnd'
      : endTime.value
        ? 'bannerWithoutCompany'
        : 'bannerBare';
    return t(`connect.support.session.${key}`, params);
  }
  const current = impersonation.value;
  if (!current) return '';
  const params = {
    name: user.value?.name || user.value?.email || '',
    impersonator: current.impersonator.name || current.impersonator.email,
    time: endTime.value,
  };
  return endTime.value
    ? t('connect.impersonation.banner', params)
    : t('connect.impersonation.bannerWithoutEnd', params);
});

async function onEnd() {
  if (leaving.value) return;
  leaving.value = true;
  try {
    if (impersonation.value) await endImpersonation();
    else await signOut();
  } finally {
    leaving.value = false;
  }
}
</script>

<template>
  <div v-if="shown" class="cx-impersonation" data-testid="shell-impersonation">
    <NxBanner variant="warning" role="status">
      {{ text }}
      <template #buttons>
        <NxButton variant="outline" type="button" :loading="leaving" @click="onEnd">
          {{ impersonation ? t('connect.impersonation.end') : t('connect.support.session.end') }}
        </NxButton>
      </template>
    </NxBanner>
  </div>
</template>

<style scoped>
.cx-impersonation {
  padding: var(--nxt-space-xs) var(--nxt-space-md) 0;
}
</style>
